/**
 * CoverageChangeSection - Section wrapper for coverage change cards.
 *
 * Sorts: created → updated → deleted → ignored.
 */

import { useMemo } from 'react';

import { Badge } from '@/components/ui/catalyst/badge';
import type { EntityChangeSchema } from '@/services/api/generated/entityChangesService';

import { CHANGE_TYPE_ORDER } from './changeTypeConfig';
import { CoverageChangeCard } from './CoverageChangeCard';

interface CoverageChangeSectionProps {
  coverages: EntityChangeSchema[];
  tasks?: EntityChangeSchema[];
  matterId?: string;
}

export const CoverageChangeSection: React.FC<CoverageChangeSectionProps> = ({
  coverages,
  tasks = [],
  matterId,
}) => {
  const sorted = useMemo(
    () =>
      [...coverages].sort(
        (a, b) =>
          CHANGE_TYPE_ORDER[a.change_type] -
          CHANGE_TYPE_ORDER[b.change_type],
      ),
    [coverages],
  );

  const tasksByCoverage = useMemo(
    () =>
      tasks.reduce<Record<string, EntityChangeSchema[]>>((map, task) => {
        const oldData = (task.old_snapshot?.data ?? {}) as Record<string, unknown>;
        const newData = (task.new_snapshot?.data ?? {}) as Record<string, unknown>;
        const coverageIds = [
          oldData.coverage_id as string | undefined,
          newData.coverage_id as string | undefined,
        ].filter(Boolean) as string[];
        coverageIds.forEach((coverageId) => {
          (map[coverageId] ??= []).push(task);
        });
        return map;
      }, {}),
    [tasks],
  );

  if (coverages.length === 0) return null;

  return (
    <div className="space-y-3">
      <div className="flex items-center gap-2">
        <h3 className="text-base font-semibold text-gray-900">Coverages</h3>
        <Badge color="zinc" className="text-xs">
          {coverages.length}
        </Badge>
      </div>
      <div className="space-y-2">
        {sorted.map((coverage) => (
          <CoverageChangeCard
            key={coverage.id}
            entity={coverage}
            relatedTasks={tasksByCoverage[coverage.entity_id]}
            matterId={matterId}
          />
        ))}
      </div>
    </div>
  );
};
